import React, { useState, useEffect, createContext, useContext } from 'react';
import { useSkin } from '@/components/SkinContext';

// ─── Types ───────────────────────────────────────────────────────────────────
interface HeroContent {
  headline: string;
  subheadline: string;
  stat1value: string;
  stat1label: string;
  stat2value: string;
  stat2label: string;
  ctaText: string;
}
interface ManifestoContent {
  title: string;
  subtitle: string;
  body: string;
  quote: string;
}
interface TrajetoriaContent {
  title: string;
  bio: string;
}
interface ContatoContent {
  heading: string;
  whatsapp: string;
  email: string;
}
export interface SiteContent {
  hero: HeroContent;
  manifesto: ManifestoContent;
  trajetoria: TrajetoriaContent;
  contato: ContatoContent;
}

// ─── Defaults ────────────────────────────────────────────────────────────────
const DEFAULTS: SiteContent = {
  hero: {
    headline: 'Arquitetura de comunicação para marcas conscientes.',
    subheadline: 'Estratégia, posicionamento e construção de presença digital com consistência.',
    stat1value: '12',
    stat1label: 'Anos de Mercado',
    stat2value: '+50',
    stat2label: 'Marcas Atendidas',
    ctaText: 'Falar com a Marina',
  },
  manifesto: {
    title: 'Comunicação não é neutra',
    subtitle: 'Ela amplifica valores, escolhas e impacto.',
    body: 'Por isso a Mídias com Propósito trabalha com marcas que entendem que crescer também é assumir responsabilidade sobre o que se comunica.',
    quote: 'Resultado financeiro importa. Mas direção importa mais.',
  },
  trajetoria: {
    title: 'Uma trajetória construída na prática',
    bio: 'Há mais de 12 anos desenvolvendo estratégias de comunicação para marcas que acreditam que negócios podem transformar o mundo. Da Serra Canastra para o Brasil, Marina Perillo une sensibilidade criativa com visão estratégica para construir presenças de marca que resistem ao tempo e ao ruído.',
  },
  contato: {
    heading: 'Vamos conversar sobre o que a sua marca precisa comunicar.',
    whatsapp: '5538991234567',
    email: 'marina@midiascomproposito.com',
  },
};

// ─── Context ─────────────────────────────────────────────────────────────────
const AdminContext = createContext<SiteContent>(DEFAULTS);
export function useAdminContent() { return useContext(AdminContext); }

function loadContent(): SiteContent {
  try {
    const raw = localStorage.getItem('mcp-content');
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULTS;
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function AdminProvider({ children }: { children: React.ReactNode }) {
  const [content, setContent] = useState<SiteContent>(loadContent);

  useEffect(() => {
    const handler = () => setContent(loadContent());
    window.addEventListener('mcp-update', handler);
    return () => window.removeEventListener('mcp-update', handler);
  }, []);

  return <AdminContext.Provider value={content}>{children}</AdminContext.Provider>;
}

// ─── Panel ────────────────────────────────────────────────────────────────────
const PASSWORD = 'marina2026';

export function AdminPanel() {
  const { activeSkin } = useSkin();
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [content, setContent] = useState<SiteContent>(loadContent);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState<'hero' | 'manifesto' | 'trajetoria' | 'contato'>('hero');

  const bg = activeSkin.bg;
  const ink = activeSkin.ink;
  const cardBg = activeSkin.card;

  function handleLogin() {
    if (pw === PASSWORD) { setAuthed(true); setPwError(''); }
    else setPwError('Senha incorreta.');
  }

  function handleSave() {
    localStorage.setItem('mcp-content', JSON.stringify(content));
    window.dispatchEvent(new Event('mcp-update'));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function handleReset() {
    if (window.confirm('Restaurar conteúdo padrão?')) {
      localStorage.removeItem('mcp-content');
      setContent(DEFAULTS);
      window.dispatchEvent(new Event('mcp-update'));
    }
  }

  function field(
    section: keyof SiteContent,
    key: string,
    label: string,
    multiline = false
  ) {
    const val = (content[section] as unknown as Record<string, string>)[key] ?? '';
    const update = (v: string) =>
      setContent((prev) => ({
        ...prev,
        [section]: { ...(prev[section] as unknown as Record<string, string>), [key]: v },
      }));
    const style: React.CSSProperties = {
      width: '100%', padding: '0.5rem',
      border: `1px solid ${ink}55`,
      borderRadius: 4, fontSize: '0.875rem',
      fontFamily: 'inherit', marginTop: 2,
      boxSizing: 'border-box',
      backgroundColor: bg, color: ink,
    };
    return (
      <div key={key} style={{ marginBottom: '0.75rem' }}>
        <label style={{ fontSize: '0.75rem', color: ink, opacity: 0.7 }}>{label}</label>
        {multiline
          ? <textarea rows={3} value={val} onChange={(e) => update(e.target.value)} style={{ ...style, resize: 'vertical' }} />
          : <input value={val} onChange={(e) => update(e.target.value)} style={style} />
        }
      </div>
    );
  }

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 48, height: 48, borderRadius: '50%',
          backgroundColor: '#524696', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}
        title="Painel de edição"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#39fffa" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>

      {/* Panel */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          display: 'flex', justifyContent: 'flex-end',
          backgroundColor: 'rgba(0,0,0,0.5)',
        }} onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div style={{
            width: 380, maxWidth: '100vw', height: '100%',
            backgroundColor: bg, overflowY: 'auto', padding: '1.5rem',
            display: 'flex', flexDirection: 'column', gap: '1rem',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ color: ink, fontWeight: 900, fontSize: '1.1rem', margin: 0 }}>✏️ Painel de Edição</h2>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: ink, fontSize: '1.25rem' }}>✕</button>
            </div>

            {!authed ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '2rem' }}>
                <p style={{ color: ink, opacity: 0.7, fontSize: '0.9rem' }}>Digite a senha para editar o conteúdo:</p>
                <input
                  type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  placeholder="Senha"
                  style={{ padding: '0.6rem', border: `1px solid ${ink}55`, borderRadius: 4, backgroundColor: cardBg, color: ink, fontSize: '1rem' }}
                />
                {pwError && <p style={{ color: '#e35f38', fontSize: '0.8rem' }}>{pwError}</p>}
                <button onClick={handleLogin} style={{ padding: '0.6rem', backgroundColor: ink, color: bg, border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>
                  Entrar
                </button>
              </div>
            ) : (
              <>
                {/* Tabs */}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {(['hero', 'manifesto', 'trajetoria', 'contato'] as const).map((t) => (
                    <button key={t} onClick={() => setTab(t)} style={{
                      padding: '0.3rem 0.7rem', borderRadius: 4, border: 'none', cursor: 'pointer',
                      backgroundColor: tab === t ? ink : cardBg,
                      color: tab === t ? bg : ink, fontSize: '0.8rem', fontWeight: 600,
                    }}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Fields */}
                <div style={{ flex: 1 }}>
                  {tab === 'hero' && <>
                    {field('hero', 'headline', 'Headline principal', true)}
                    {field('hero', 'subheadline', 'Subtítulo', true)}
                    {field('hero', 'stat1value', 'Estatística 1 — Valor')}
                    {field('hero', 'stat1label', 'Estatística 1 — Label')}
                    {field('hero', 'stat2value', 'Estatística 2 — Valor')}
                    {field('hero', 'stat2label', 'Estatística 2 — Label')}
                    {field('hero', 'ctaText', 'Texto do botão CTA')}
                  </>}
                  {tab === 'manifesto' && <>
                    {field('manifesto', 'title', 'Título', true)}
                    {field('manifesto', 'subtitle', 'Eyebrow (subtítulo)')}
                    {field('manifesto', 'body', 'Corpo do texto', true)}
                    {field('manifesto', 'quote', 'Citação de destaque', true)}
                  </>}
                  {tab === 'trajetoria' && <>
                    {field('trajetoria', 'title', 'Título da seção')}
                    {field('trajetoria', 'bio', 'Texto biográfico', true)}
                  </>}
                  {tab === 'contato' && <>
                    {field('contato', 'heading', 'Heading da seção', true)}
                    {field('contato', 'whatsapp', 'WhatsApp (somente números)')}
                    {field('contato', 'email', 'E-mail')}
                  </>}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.5rem' }}>
                  <button onClick={handleSave} style={{
                    flex: 1, padding: '0.6rem', backgroundColor: ink, color: bg,
                    border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700,
                  }}>
                    {saved ? '✓ Salvo!' : 'Salvar'}
                  </button>
                  <button onClick={handleReset} style={{
                    padding: '0.6rem 1rem', backgroundColor: 'transparent',
                    color: ink, border: `1px solid ${ink}55`,
                    borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem',
                  }}>
                    Restaurar padrão
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
